/**
 * Credit Balance Validation Utilities
 *
 * Provides functions to check credit balance and handle insufficient balance scenarios.
 */

import { useAuthStore } from '@/store/useAuthStore'
import { useModalStore } from '@/store/useModalStore'
import { checkBalance, CreditEstimateScenario } from '@/services/creditEstimateService'

export interface ValidateCreditBalanceParams {
  /** Required minimum balance */
  requiredMinimumBalance: number
  /** Current user balance */
  currentBalance: number
}

export interface ValidationResult {
  /** Whether balance is sufficient */
  valid: boolean
  /** Amount of credits short (0 if sufficient) */
  shortfall: number
}

/**
 * Validate if current balance meets the required minimum
 */
export function validateCreditBalance(
  params: ValidateCreditBalanceParams
): ValidationResult {
  const { requiredMinimumBalance, currentBalance } = params
  const shortfall = Math.max(0, requiredMinimumBalance - currentBalance)

  return {
    valid: shortfall === 0,
    shortfall,
  }
}

/**
 * Check balance and show modal if insufficient
 *
 * @returns true if balance is sufficient, false if modal is shown
 */
export function validateAndShowModal(
  requiredMinimumBalance: number,
  currentBalance: number
): boolean {
  const { valid, shortfall } = validateCreditBalance({
    requiredMinimumBalance,
    currentBalance,
  })

  if (valid) {
    return true
  }

  // Show the insufficient credits modal
  useModalStore.getState().openInsufficientCreditsModal({
    currentBalance,
    requiredBalance: requiredMinimumBalance,
    shortfall,
  })

  return false
}

/**
 * Get current user's credit balance from auth store
 */
export function getCurrentBalance(): number {
  const user = useAuthStore.getState().user
  return user?.credits ?? 0
}

/**
 * Combined validation: estimate + check + show modal
 *
 * This is a convenience function that performs the full validation flow:
 * 1. Calls the backend to check balance for a scenario
 * 2. If insufficient, shows the modal
 *
 * @returns true if balance is sufficient, false if modal is shown
 */
export async function validateScenarioAndShowModal(
  scenario: CreditEstimateScenario,
  params: Record<string, any>
): Promise<boolean> {
  try {
    const result = await checkBalance(scenario, params)

    if (result.sufficient) {
      return true
    }

    // Show the insufficient credits modal
    useModalStore.getState().openInsufficientCreditsModal({
      currentBalance: result.current_balance,
      requiredBalance: result.required_balance,
      shortfall: result.shortfall,
    })

    return false
  } catch (error) {
    console.error('Balance check failed:', error)
    // On error, allow the operation to proceed (backend will do final check)
    return true
  }
}

// Legacy function for backwards compatibility
export function validateAndNavigate(
  requiredMinimumBalance: number,
  currentBalance: number,
  _navigate: unknown // Not used anymore, kept for API compatibility
): boolean {
  return validateAndShowModal(requiredMinimumBalance, currentBalance)
}
